/** Shared English strings used by all three applications (namespace "common"). */
export const commonEn = {
  appName: 'Arabat POS',
  interfaces: {
    manager: 'Manager',
    pos: 'Point of Sale',
    platform: 'Platform Administration',
  },
  language: {
    label: 'Language',
    ar: 'العربية',
    en: 'English',
  },
  lock: {
    title: 'POS locked',
    body: 'Enter your password to continue. Anyone else must sign in with their own account.',
    unlock: 'Unlock',
    otherUser: 'Sign in as someone else',
    lockNow: 'Lock',
  },
  actions: {
    close: 'Close',
    retry: 'Try again',
    signIn: 'Sign in',
    signOut: 'Sign out',
    back: 'Back',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },
  status: {
    loading: 'Loading…',
    apiOnline: 'Server connected',
    apiDegraded: 'Server degraded',
    apiOffline: 'Server unreachable',
    apiChecking: 'Checking server…',
  },
  auth: {
    email: 'Email',
    password: 'Password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    passwordHint: 'At least 10 characters.',
    passwordsDiffer: 'Passwords do not match.',
    fullName: 'Full name',
    showPassword: 'Show',
    hidePassword: 'Hide',
    forgotPassword: 'Forgot your password?',
    backToSignIn: 'Back to sign in',
    chooseMerchant: 'Choose a company',
    chooseBranch: 'Choose a branch',
    forgotTitle: 'Reset your password',
    forgotBody: 'Enter your e-mail. If an account exists, we will send a reset link.',
    forgotSend: 'Send reset link',
    forgotSent:
      'If an account exists for this e-mail, a reset link has been sent. Check your inbox.',
    resetTitle: 'Choose a new password',
    resetSubmit: 'Save new password',
    resetDone: 'Your password was changed. Sign in with the new password.',
    resetMissingToken: 'This reset link is incomplete. Request a new one.',
    useOtherApp: 'Your account is set up for another application.',
    openApp: 'Open {{app}}',
    mfaTitle: 'Two-factor verification',
    mfaBody: 'Enter the 6-digit code from your authenticator app.',
    mfaCode: 'Verification code',
    verify: 'Verify',
    noAccount: 'New company?',
    createAccount: 'Create an account',
    haveAccount: 'Already registered?',
    accessDenied: 'You do not have permission to view this page.',
    switchTo: 'Switch to {{app}}',
  },
  invite: {
    title: 'Join your team',
    intro: '{{name}}, you were invited to join {{company}} as',
    existing: 'An account with this e-mail already exists. Confirm with your current password.',
    submit: 'Accept invitation',
    done: 'Your account is ready. You can sign in now.',
    invalid: 'This invitation link is incomplete.',
  },
  input: {
    wholeNumber: 'Enter a whole number from {{min}} to {{max}}.',
    wholeNumberMin: 'Enter a whole number of at least {{min}}.',
  },
  theme: {
    label: 'Appearance',
    light: 'Light',
    dark: 'Dark',
    system: 'Auto',
  },
  nav: {
    comingSoon: 'Coming in {{milestone}}',
  },
  paging: {
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{pages}}',
  },
  invoice: {
    print: 'Print invoice',
    pdf: 'Download PDF',
    failed: 'The invoice could not be prepared. Try again.',
    title: 'Invoice {{number}}',
    send: 'Send to the customer on WhatsApp',
    sharePdf: 'Share the PDF',
    shareReady: 'Share the PDF now',
    call: 'Call the customer',
    message:
      'Hello {{name}}, thank you for your order from {{store}}. Your invoice {{number}}: {{url}}',
    downloaded: 'This browser cannot share files: the PDF was downloaded.',
    tapAgain: 'The PDF is ready: tap “Share the PDF now”.',
    notFound: 'Invoice not found',
    notFoundHint: 'Check the link, or ask the store to send it again.',
  },
  errors: {
    title: 'Something went wrong',
    notFoundTitle: 'Page not found',
    notFoundBody: 'The page you are looking for does not exist.',
    requestId: 'Reference: {{id}}',
    UNAUTHENTICATED: 'Please sign in to continue.',
    FORBIDDEN: 'You do not have permission to perform this action.',
    VALIDATION_FAILED: 'Some fields are invalid. Please review them.',
    NOT_FOUND: 'The requested item was not found.',
    ROUTE_NOT_FOUND: 'The requested address does not exist.',
    CONFLICT: 'This record was changed by someone else. Reload and try again.',
    RATE_LIMITED: 'Too many attempts. Please wait and try again.',
    SERVICE_UNAVAILABLE: 'The service is temporarily unavailable.',
    INVALID_CREDENTIALS: 'The e-mail or password is incorrect.',
    ACCOUNT_DISABLED: 'This account has been disabled. Contact your manager.',
    NO_MERCHANT_ACCESS: 'Your account is not active in any company.',
    INTERFACE_NOT_PERMITTED: 'You are not authorized for this application.',
    PASSWORD_REQUIRED:
      'You signed in with a PIN. Sign in with your e-mail and password to open the management app or another company.',
    TICKET_INVALID: 'This step has expired. Please sign in again.',
    MFA_INVALID: 'The verification code is incorrect.',
    RESET_TOKEN_INVALID: 'This reset link is invalid or has expired.',
    EMAIL_ALREADY_REGISTERED: 'This e-mail is already registered. Sign in or reset your password.',
    VERSION_CONFLICT: 'This record was changed by someone else. Reload and try again.',
    PRIVILEGE_ESCALATION: 'You cannot grant permissions beyond your own authority.',
    INVITATION_INVALID: 'This invitation is invalid, expired or already used.',
    LAST_SUPER_ADMIN:
      'This would leave no active super admin. Promote or enable another super admin first.',
    OPERATOR_SELF_CHANGE: 'You cannot disable your own account or change your own role.',
    ROLE_IN_USE: 'This role is still assigned to employees or invitations.',
    DUPLICATE_CODE: 'This code is already used.',
    ALREADY_MEMBER: 'This person is already an employee of the company.',
    DUPLICATE_SKU: 'Another product already uses this SKU.',
    DUPLICATE_BARCODE: 'Another product already uses this barcode.',
    INVALID_PRICE: 'Invalid amount: use up to 3 decimal places.',
    UNSUPPORTED_MEDIA: 'Only JPEG, PNG and WebP images are accepted.',
    INSUFFICIENT_STOCK: 'Not enough stock for one or more items.',
    NOT_STOCK_TRACKED:
      'Some items cannot be adjusted: stock is not tracked or the item is archived.',
    OPENING_STOCK_EXISTS:
      'Opening stock is already set for this item. Use an adjustment or a count.',
    INVALID_QUANTITY: 'Invalid quantity: use a positive number with up to 3 decimal places.',
    IDEMPOTENCY_CONFLICT:
      'This request was already submitted with different content. Reload and try again.',
    BRANCH_INACTIVE: 'This branch is inactive.',
    SHIFT_REQUIRED: 'Open a shift first.',
    SHIFT_ALREADY_OPEN: 'You already have an open shift.',
    REGISTER_BUSY: 'This register is in use by another shift.',
    NOT_PRIMARY_DEVICE:
      'Only a device bound to a register does this (shift, cash, browser receipts).',
    CASH_NOT_ALLOWED: 'This device has no cash drawer: card or transfer only.',
    REGISTER_BOUND:
      'This register is bound to another device, or the device is still bound. Unbind first.',
    PIN_LINK_INVALID: 'This sign-in link is no longer valid. Ask your manager for the new one.',
    PIN_NOT_SET: 'You have no PIN yet. Set it from the till app, then try again.',
    DISCOUNT_ARCHIVED: 'This discount is archived and can no longer be changed.',
    DOMAINS_UNAVAILABLE: 'Connecting your own domain is not available yet.',
    DOMAINS_PROVIDER_ERROR: 'The domain service did not answer. Try again in a few minutes.',
    DOMAIN_SEARCH_UNAVAILABLE: 'Domain search is not available yet.',
    DOMAIN_NOT_ALLOWED: 'This domain belongs to Arabat. Type the domain you bought.',
    DOMAIN_TAKEN: 'This domain is connected to another store.',
    WHATSAPP_UNAVAILABLE: 'WhatsApp messages are not available yet.',
    WHATSAPP_NOT_VERIFIED: 'Confirm the WhatsApp number with its code first.',
    WHATSAPP_SEND_FAILED:
      'The code could not be sent on WhatsApp. Check that the number uses WhatsApp.',
    CODE_RESEND_TOO_SOON: 'Wait a moment before sending a new code.',
    CODE_EXPIRED: 'The code has expired. Send a new one.',
    CODE_TOO_MANY_ATTEMPTS: 'Too many tries. Send a new code.',
    CODE_INVALID: 'Wrong code.',
    DOMAIN_ALREADY_CONNECTED: 'Remove the connected domain first.',
    DOMAIN_NOT_ACTIVE: 'The domain can be the main address once it works.',
    STORE_NOT_SAVED: 'Save the store settings first.',
    GOOGLE_CONNECT_UNAVAILABLE: 'Connecting with Google is not available right now.',
    STORE_NOT_PUBLISHED: 'Publish the store first.',
    CUSTOM_DOMAIN_REQUIRED:
      'Google features work once your own domain is connected and is the main address of your store.',
    DUPLICATE_HANDLE: 'Another page of your store already uses this address.',
    REGISTER_INACTIVE: 'This register is inactive.',
    REGISTER_ACCESS_DENIED: 'You are not allowed to use this register.',
    SHIFT_CLOSED: 'This shift is already closed.',
    INVALID_BREAKDOWN: 'The counted notes and coins do not match the total.',
    INSUFFICIENT_CASH: 'The drawer does not hold this much cash.',
    INSUFFICIENT_TREASURY_CASH: 'The treasury does not hold this much cash.',
    TREASURY_NOT_COUNTED: 'Record the treasury’s opening count first.',
    EXPECTED_CHANGED: 'The balance changed meanwhile. Count again.',
    REGISTER_HAS_CASH: 'Move the cash out of the register first.',
    TRANSFER_NOT_ALLOWED: 'This cash transfer is not allowed.',
    INVALID_DISCOUNT: 'Invalid discount.',
    DISCOUNT_LIMIT_EXCEEDED: 'This discount is above your limit. Ask a supervisor.',
    PRICE_OVERRIDE_DENIED: 'You are not allowed to change prices.',
    PRICE_OUT_OF_BOUNDS: 'This price is outside the limits set by the company for price changes.',
    ZERO_TOTAL_DENIED: 'A sale with nothing to pay needs a supervisor.',
    REASON_REQUIRED: 'A reason is required for this.',
    OVERRIDE_INVALID: 'The supervisor approval has expired or was already used. Ask again.',
    OVERRIDE_SELF: 'A supervisor override must come from another person.',
    OVERRIDE_NOT_ALLOWED: 'This person cannot authorise anything beyond your own rights here.',
    REFERENCE_REQUIRED: 'Enter the terminal or transfer reference for this payment.',
    SETTLEMENT_ACCOUNT_REQUIRED:
      'This payment method has no settlement account. Ask a manager to link one before using it.',
    PAYMENT_TOTAL_MISMATCH: 'The payments do not add up to the total.',
    TOTAL_CHANGED: 'Prices changed. Review the sale and try again.',
    PAYMENT_NOT_VERIFIED: 'This payment cannot be verified yet.',
    PAYMENT_METHOD_UNAVAILABLE: 'This payment method is not available.',
    ITEM_UNAVAILABLE: 'Some items cannot be sold at this branch.',
    ORDER_NOT_SUSPENDED: 'This held sale was already completed or discarded.',
    REFUND_EXCEEDS_SALE: 'This is more than can still be returned for this sale.',
    REFUND_ALLOCATION_INVALID: 'The refund cannot be paid back this way.',
    REFUND_NOT_PENDING: 'This refund is no longer waiting for a decision.',
    NOT_VOIDABLE: 'Only sales of your open shift without returns can be cancelled.',
    SELF_APPROVAL: 'You cannot approve your own refund.',
    APPROVAL_REQUIRED: 'This needs a supervisor’s approval.',
    DEPOSIT_SETTLED: 'This deposit was already confirmed or returned.',
    ACCOUNT_IN_USE:
      'This bank account still has a balance, unreconciled entries or a deposit in transit. Settle them first.',
    ACCOUNT_KIND_LOCKED:
      'This bank account already has movements, so it stays a bank account. Create a new account of the other type instead.',
    ADJUSTMENT_DECIDED: 'This adjustment was already decided.',
    STOCK_MOVED_SINCE_COUNT:
      'Stock moved while you were counting. Review the movements and confirm the count.',
    STOCK_RECOUNTED:
      'These items were counted again after this count. Reject it; the newer count stands.',
    OWNER_FROZEN: 'The owner account of this company is frozen by the platform. Contact support.',
    OWNER_ONLY_SETTING: 'Only the owner can change approval limits and approval policies.',
    TRANSFER_INVALID:
      'This ownership transfer is invalid, expired, cancelled or already completed.',
    GRANTS_NEED_CONFIRMATION:
      'Dormant role assignments at this branch would come back into force. Confirm keeping or clearing them.',
    PERIOD_CLOSED: 'This date falls in a closed period. Closed periods accept no new documents.',
    PERIOD_NOT_CLOSABLE:
      'This month cannot be closed yet: it has not ended, an earlier month is still open, or tills may still upload sales made in it (device offline limit).',
    TIMEZONE_LOCKED:
      'The time zone cannot be changed once a month has been closed: closed months keep their cut-off.',
    STOCK_ON_HAND:
      'This item still has stock on hand. Write it off or transfer it before archiving it or switching tracking off.',
    BRANCH_IN_USE:
      'This branch still has stock, cash, open shifts or open documents. Settle them before deactivating it.',
    COST_UNKNOWN: 'Some items have no cost yet. Set a cost on the product before dispatching them.',
    LOSS_DECIDED: 'This transfer loss was already decided.',
    PAYLOAD_TOO_LARGE: 'The file is too large.',
    STORE_CLOSED: 'This store is not taking orders right now.',
    GATEWAY_NOT_CONFIGURED: 'Online payment is not available yet. Choose another method.',
    NO_PAYMENT_OPTION:
      'Allow payment on receipt, or enable an online payment method that can take payments.',
    PAYMENT_METHOD_REQUIRED: 'This store needs the order to be paid online.',
    ORDER_UNPAID: 'Record the payment before completing the order.',
    RENAME_LIMIT: 'The store address can be changed once a day.',
    SLOT_FULL: 'This time is fully booked. Choose another.',
    PLACE_TAKEN: 'Some of these areas already belong to another delivery area.',
    LAST_STORE_BRANCH: 'The store needs at least one active branch to prepare its orders.',
    OUTSIDE_HOURS: 'The store is closed right now.',
    PROMOTION_INVALID: 'This promotion code cannot be used.',
    OPTIONS_INVALID: 'The choices of an item changed. Choose them again from its page.',
    ACCOUNTS_DISABLED: 'This store does not offer accounts.',
    EMAIL_TAKEN: 'An account with this e-mail already exists.',
    PHONE_TAKEN: 'An account with this phone already exists.',
    ASSET_IN_USE: 'This picture is used in the store design. Remove it from the sections first.',
    ASSET_LIMIT_REACHED: 'The store holds at most 200 design pictures. Delete some first.',
    PAYMENT_SIGNATURE_INVALID: 'The payment notification could not be verified.',
    MINIMUM_ORDER: 'The order is below the store’s minimum.',
    LINK_LIMIT_REACHED: 'A link page holds at most 50 links.',
    PLAN_LIMIT_REACHED:
      'Your subscription plan limit is reached. Ask the platform to change your plan.',
    PLAN_FEATURE_UNAVAILABLE: 'Your subscription plan does not include this feature.',
    PLAN_CHANGE_BLOCKED: 'The merchant’s current usage does not fit this plan.',
    MERCHANT_SUSPENDED: 'This company account is suspended. Contact the platform support.',
    SESSION_LOCKED: 'The POS is locked. Enter your password.',
    UNLOCK_SHARED_ACCOUNT:
      'This person also works for another company, which this sign-in lock protects too. Send them a password reset instead.',
    MALFORMED_REQUEST: 'The request could not be read. Reload the page and try again.',
    NETWORK_BLOCKED:
      'The network you are using (such as a work network) blocked this request with a web filter. Try mobile data, or ask your IT team to allow arabatapp.com.',
    METHOD_NOT_ALLOWED: 'This action is not available here. Reload the page and try again.',
    INTERNAL_ERROR: 'Something went wrong on our side. Try again in a moment.',
    ORIGIN_REJECTED: 'This request came from an unknown address. Open the app from its usual link.',
    MOVEMENT_DECIDED: 'This request was already approved or rejected. Reload the page.',
    PENDING_MOVEMENTS:
      'A cash-out is still waiting for a manager. Withdraw it or wait for the decision.',
    ORDER_OVERPAID: 'This payment is more than what is still owed on the purchase order.',
    ASSISTANT_DISABLED: 'The assistant is not available right now.',
    INVALID_STATE: 'This cannot be done in its current state. Reload the page and check it.',
    RECEIVE_EXCEEDS_ORDER: 'This is more than is still open on the order. Check the quantities.',
    SUPPLIER_INACTIVE: 'This supplier is inactive. Activate it or choose another one.',
    CUSTOMER_INACTIVE: 'This customer is inactive. Choose another customer or activate this one.',
    DUPLICATE_PHONE: 'Another customer already has this phone number.',
    MODULE_DISABLED: 'The restaurant module is not turned on for this company.',
    TABLE_OCCUPIED: 'This table already has an open order. Open that order instead.',
    ITEM_ALREADY_PAID: 'This item is already paid. Use a refund instead.',
    MODIFIER_INVALID: 'The chosen add-ons do not fit this item. Review the choices.',
    DEVICE_REVOKED: 'This device was removed. Register it again.',
    NOT_OWN_SALE: 'A device can only reprint or change its own sales.',
    RECEIPT_NUMBER_INVALID: 'This receipt number is not valid for this device.',
    RECEIPT_NUMBER_TAKEN: 'This receipt number is already used.',
    TOTALS_MISMATCH: 'The sale totals do not add up. Ask a manager to review it.',
    UNKNOWN_VARIANT: 'An item in this sale no longer exists. Ask a manager to review it.',
    MEMBER_NOT_ALLOWED: 'This employee was not active in the company at that time.',
    WRONG_DEVICE: 'This belongs to another device or branch.',
    SHIFT_NOT_ACCEPTED:
      'The server refused this shift. Selling stays blocked until it is accepted.',
    NETWORK: 'Cannot reach the server. Check your connection.',
    UNKNOWN: 'An unexpected error occurred.',
  },
};

export type CommonResources = typeof commonEn;
